
import { useRef } from "react";
import { PortfolioData } from "@/pages/DigitalPortfolio";
import { 
  Layout, 
  Image, 
  FileText, 
  Video, 
  Briefcase,
  PanelLeft,
  Palette,
  Type,
  Plus
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { generateRandomId } from "@/lib/utils";

interface PortfolioSidebarProps {
  portfolio: PortfolioData;
  updatePortfolio: (updates: Partial<PortfolioData>) => void;
}

export const PortfolioSidebar = ({
  portfolio,
  updatePortfolio
}: PortfolioSidebarProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const addItem = (type: "image" | "text" | "video" | "project") => {
    if (type === "image" && fileInputRef.current) {
      fileInputRef.current.click();
      return;
    }
    
    const newItem = {
      id: generateRandomId(),
      type,
      content: type === "text" ? "Add your text here..." : "",
      title: `New ${type} item`,
      description: "",
      tags: [],
    };
    
    updatePortfolio({
      items: [...portfolio.items, newItem]
    });
  };
  
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // In a real app, you would upload this to a server
    // For now, we'll use a local URL
    const reader = new FileReader();
    reader.onload = () => {
      const newItem = {
        id: generateRandomId(),
        type: "image" as const,
        content: reader.result as string,
        title: file.name,
        description: "",
        tags: [],
      };
      
      updatePortfolio({
        items: [...portfolio.items, newItem]
      });
    };
    reader.readAsDataURL(file);
    
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Hidden file input for image uploads */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleImageUpload}
      />
      
      {/* Add Items Section */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-semibold flex items-center">
            <PanelLeft className="h-4 w-4 mr-2" />
            Add Content
          </h3>
          
          <div className="grid grid-cols-2 gap-2">
            <Button 
              variant="outline" 
              className="flex flex-col items-center justify-center h-20 p-2" 
              onClick={() => addItem("image")}
            >
              <Image className="h-6 w-6 mb-1" />
              <span className="text-xs">Image</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="flex flex-col items-center justify-center h-20 p-2"
              onClick={() => addItem("text")}
            >
              <FileText className="h-6 w-6 mb-1" />
              <span className="text-xs">Text</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="flex flex-col items-center justify-center h-20 p-2"
              onClick={() => addItem("video")}
            >
              <Video className="h-6 w-6 mb-1" />
              <span className="text-xs">Video</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="flex flex-col items-center justify-center h-20 p-2"
              onClick={() => addItem("project")}
            >
              <Briefcase className="h-6 w-6 mb-1" />
              <span className="text-xs">Project</span>
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Styling Section */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-semibold flex items-center">
            <Layout className="h-4 w-4 mr-2" />
            Styling
          </h3>
          
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="color" className="text-xs flex items-center">
                <Palette className="h-3 w-3 mr-1" /> Color Theme
              </Label>
              <Input
                id="color"
                type="color"
                value={portfolio.color}
                onChange={(e) => updatePortfolio({ color: e.target.value })}
                className="w-full h-8"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="font" className="text-xs flex items-center">
                <Type className="h-3 w-3 mr-1" /> Font
              </Label>
              <select
                id="font"
                value={portfolio.font}
                onChange={(e) => updatePortfolio({ font: e.target.value })}
                className="w-full h-8 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Inter">Inter</option>
                <option value="Roboto">Roboto</option>
                <option value="Playfair Display">Playfair Display</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Open Sans">Open Sans</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Items List Section */}
      {portfolio.items.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <h3 className="text-sm font-semibold">Portfolio Items</h3>
            
            <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1">
              {portfolio.items.map((item) => {
                const ItemIcon = item.type === "image" 
                  ? Image 
                  : item.type === "text" 
                    ? FileText 
                    : item.type === "video" 
                      ? Video 
                      : Briefcase;
                
                return (
                  <div 
                    key={item.id}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer"
                  >
                    <ItemIcon className="h-4 w-4 text-gray-500" />
                    <span className="text-sm truncate flex-1">{item.title}</span>
                  </div>
                );
              })}
            </div>
            
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full mt-2 text-xs"
              onClick={() => addItem("text")}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Another Item
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
